import type { Catalogue } from '../types';

/**
 * French catalogue. Complete at launch and complete at all times (PRD NFR-006):
 * the `Catalogue` type makes a missing or misspelled key a compile error, and
 * `completeness.test.ts` fails CI if a key or a plural form is missing.
 *
 * The `stage.*` strings are copied verbatim from the PRD GST-036 table.
 *
 * Voice: playful franglais, premium look (PRD section 1.4, WEB-024).
 */

export const fr: Catalogue = {
  // -------------------------------------------------------------------------
  // Brand
  // -------------------------------------------------------------------------
  'brand.name': 'Croissant in Bed',
  'brand.tagline': 'Restez au lit. On apporte le croissant.',

  // -------------------------------------------------------------------------
  // Guest stage model - PRD GST-036. Verbatim; do not paraphrase.
  // -------------------------------------------------------------------------
  'stage.orderPlaced.title': 'Commande passée',
  'stage.orderConfirmed.title': 'Commande confirmée',
  'stage.preparing.title': 'Préparation',
  'stage.preparing.message': 'Votre boulanger prépare votre commande',
  'stage.orderReady.title': 'Commande prête pour le retrait',
  'stage.orderReady.message': 'Votre commande est prête à la boulangerie',
  'stage.findingCourier.title': 'Recherche d’un livreur',
  'stage.findingCourier.message': 'Nous cherchons un livreur pour récupérer votre commande',
  'stage.courierAssigned.title': 'Livreur assigné',
  'stage.courierAssigned.message': '{name} se rend à la boulangerie',
  'stage.onTheWay.title': 'En route',
  'stage.onTheWay.message': '{name} a récupéré votre commande et est en route',
  'stage.arriving.title': 'Arrivée imminente',
  'stage.arriving.message': '{name} arrive - préparez-vous à récupérer votre commande',
  'stage.delivered.title': 'Livrée',
  'stage.delivered.message': 'Votre commande a été livrée',

  // -------------------------------------------------------------------------
  // Mots des pastilles (#2004) - voir en.ts
  // -------------------------------------------------------------------------
  'pill.payout.PENDING': 'En attente',
  'pill.payout.PAID': 'Payé',
  'pill.payout.FAILED': 'Échec',
  'pill.payout.CANCELED': 'Annulé',
  'pill.orderSummary.PAYMENT_PENDING': 'Paiement en attente',
  'pill.orderSummary.AWAITING_BAKERY': 'En attente de la boulangerie',
  'pill.orderSummary.PREPARING': 'En préparation',
  'pill.orderSummary.READY': 'Prête',
  'pill.orderSummary.ON_THE_WAY': 'En route',
  'pill.orderSummary.DELIVERED': 'Livrée',
  'pill.orderSummary.CANCELLED': 'Annulée',
  // Why an order reads as it does (#2004, section 3), for Partner's bakery
  // orders; Internal reads the English.
  'pill.orderSummary.reason.CASCADE_EXHAUSTED': 'Aucune boulangerie n’a accepté',
  'pill.orderSummary.reason.NO_ELIGIBLE_HOUSE': 'Aucune boulangerie ne dessert le logement',
  'pill.orderSummary.reason.WINDOW_SWEEP': 'Personne n’a répondu',
  'pill.orderSummary.reason.COUNTER_GAVE_BACK': 'Rendue par la boulangerie',
  'pill.orderSummary.reason.NEVER_PREPARED': 'Jamais préparée',
  'pill.orderSummary.reason.CLOSURE': 'Boulangerie fermée',
  'pill.orderSummary.reason.GUEST': 'Annulée par le client',
  'pill.orderSummary.reason.OPERATOR': 'Annulée par nos équipes',
  'pill.orderSummary.reason.NO_COURIER': 'Aucun livreur trouvé',
  'pill.orderSummary.reason.NEVER_COLLECTED': 'Jamais récupérée',
  'pill.orderSummary.reason.DELIVERY_FAILED': 'Livraison échouée',
  'pill.orderSummary.reason.PROPERTY_REMOVED': 'Logement retiré',
  'pill.orderSummary.reason.NEVER_PAID': 'Carte refusée',
  'pill.orderSummary.reason.SEARCHING_UNTIL': 'Pas encore de livreur, recherche jusqu’à {time}',
  'pill.orderSummary.reason.NO_COURIER_YET': 'Pas encore de livreur',
  // Internal Ops only: English.
  'pill.orderLifecycle.reason.SCHEDULED': 'Scheduled',
  'pill.orderLifecycle.reason.OPEN_TO_ANSWER': 'Open to answer',
  'pill.orderLifecycle.reason.RINGING': 'Ringing at the counter',
  'pill.orderLifecycle.reason.CLOSED': 'Closed',
  'pill.courierJob.CREATED': 'Searching',
  'pill.courierJob.COURIER_ASSIGNED': 'Assigned',
  'pill.courierJob.PICKED_UP': 'In transit',
  'pill.courierJob.ARRIVING': 'Arriving',
  'pill.courierJob.RETURNING': 'Returning',
  'pill.courierJob.DELIVERED': 'Delivered',
  'pill.courierJob.CANCELLED': 'Canceled',
  'pill.courierJob.FAILED': 'Failed',
  'pill.latenessBand.LATE': 'Late',
  'pill.latenessBand.AT_RISK': 'At risk',
  'pill.latenessBand.WATCH': 'Watch',
  'pill.latenessBand.ON_TRACK': 'On track',
  'pill.timingVerdict.ON_TIME': 'OK',
  'pill.timingVerdict.EARLY': 'Early',
  'pill.timingVerdict.LATE': 'Late',
  'pill.timingVerdict.MISSED': 'Missed',
  'pill.timingVerdict.PENDING': 'Pending',
  'pill.tag.ORDERING_CLOSED': 'Ordering closed',
  'pill.tag.NOW': 'Now',
  'pill.tag.ROBOBAKER': 'Robobaker',
  'pill.tag.SERVES_NOWHERE': 'Serves nowhere',
  'pill.tag.SIMULATION': 'Simulation',
  'pill.tag.DEV_SESSION': 'Session dev',
  'pill.tag.MULTI_DAY_STAY': 'Séjour',
  // Internal Ops, the counters and the service: English-only, so the English words.
  'pill.bakeryService.OPEN': 'Open',
  'pill.bakeryService.UNREACHABLE': 'Offline',
  'pill.bakeryService.CLOSED': 'Closed',
  'pill.bakeryService.PAUSED': 'Paused',
  'pill.bakeryService.INCOMPLETE': 'Not ready',
  'pill.bakeryService.SUSPENDED': 'Suspended',
  'pill.bakeryService.reason.NO_DEVICE_ANSWERING': 'No device answering',
  'pill.bakeryService.reason.NO_DEVICE_PAIRED': 'No device paired',
  'pill.bakeryService.reason.CLOSURE_THROUGH': 'Closure through {date}',
  'pill.bakeryService.reason.BAKER': 'By the baker',
  'pill.bakeryService.reason.KILL_SWITCH': 'Kill switch on',
  'pill.bakeryService.reason.TWO_UNANSWERED': 'Two orders unanswered',
  'pill.bakeryService.reason.PICKUP_DETAILS': 'Pickup details missing',
  'pill.bakeryService.reason.PAYOUT_SETUP': 'Payout setup unfinished',
  'pill.bakeryService.reason.VAT': 'VAT not stated',
  'pill.deviceConnectivity.READY': 'Online',
  'pill.deviceConnectivity.UNREACHABLE': 'Offline',
  'pill.deviceConnectivity.reason.IN_BACKGROUND': 'In background',
  'pill.device.ONLINE': 'Online',
  'pill.device.UPDATING': 'Updating',
  'pill.device.OFFLINE': 'Offline',
  'pill.device.BLOCKED': 'Blocked',
  'pill.device.reason.IN_BACKGROUND': 'In background',
  'pill.device.reason.CHARGING': 'Last seen charging',
  'pill.device.reason.UPDATE_REQUIRED': 'Update required',
  'pill.standing.ACTIVE': 'Active',
  'pill.standing.SUSPENDED': 'Suspended',
  'pill.standing.INCOMPLETE': 'Not ready',
  'pill.healthVerdict.GREEN': 'OK',
  'pill.healthVerdict.AMBER': 'Warning',
  'pill.healthVerdict.RED': 'Blocked',
  'pill.accountingExport.ON': 'On',
  'pill.accountingExport.OFF': 'Off',
  'pill.alertSeverity.DANGER': 'Serious',
  'pill.alertSeverity.WARNING': 'Warning',
  'pill.alertSeverity.INFO': 'Information',
  'pill.deadLetter.OPEN': 'Open',
  'pill.releaseStep.HALTED': 'Halted',
  'pill.scenarioRun.PASS': 'Pass',
  'pill.scenarioRun.FAIL': 'Fail',
  'pill.scenarioRun.INCONCLUSIVE': 'Inconclusive',
  // The Devices dialog both portals share (Internal and Partner).
  'pill.pairedDevice.ONLINE': 'En ligne',
  'pill.pairedDevice.UNREACHABLE': 'Hors ligne',
  'pill.pairedDevice.reason.IN_BACKGROUND': 'En arrière-plan',
  // Internal Ops, the money: English-only, so the English words.
  'pill.payableAccount.READY': 'Payable',
  'pill.payableAccount.BLOCKED': 'Blocked',
  'pill.payableAccount.FUNDS_PENDING': 'Pending',
  'pill.payableAccount.reason.NEGATIVE_BALANCE': 'Negative balance',
  'pill.payableAccount.reason.FUNDS_HELD': 'Funds held',
  'pill.payableAccount.reason.BANK_DETAILS': 'Bank details missing',
  'pill.payableAccount.reason.RESTRICTED': 'Restricted by Stripe',
  'pill.payableAccount.reason.IDENTITY': 'Identity check missing',
  'pill.payableAccount.reason.ON_ITS_WAY': 'Payout on its way',
  'pill.payableAccount.reason.FUNDS_NOT_AVAILABLE': 'Funds not available yet',
  'pill.monthlyPayout.READY': 'Payable',
  'pill.monthlyPayout.FUNDS_PENDING': 'Pending',
  'pill.monthlyPayout.NOTHING': 'Nothing',
  'pill.monthlyPayout.PAID': 'Paid',
  'pill.monthlyPayout.FAILED': 'Failed',
  'pill.monthlyPreview.READY': 'Payable',
  'pill.monthlyPreview.FUNDS_PENDING': 'Pending',
  'pill.monthlyPreview.NOTHING': 'Nothing',
  'pill.monthlyPreview.NOT_CLOSED': 'In progress',
  'pill.monthlyPreview.PAID': 'Paid',
  'pill.payoutMovement.PENDING': 'Pending',
  'pill.figure.ESTIMATED': 'Estimated',
  'pill.figure.PENDING': 'Pending',
  'pill.reconciliation.CLEAN': 'No drift',
  'pill.reconciliation.DRIFT': 'Drift',
  'pill.deliveryStatement.OPEN': 'In progress',
  'pill.deliveryStatement.UNMATCHED': 'Unmatched',
  'pill.deliveryStatement.MATCHED': 'Matched',
  'pill.deliveryStatement.DIFFERS': 'Differs',
  'pill.documentExport.PENDING': 'Pending',
  'pill.documentExport.EXPORTED': 'Exported',
  'pill.documentExport.FAILED': 'Failed',
  'pill.payoutAccount.NOT_STARTED': 'Incomplete',
  'pill.payoutAccount.PENDING': 'Pending',
  'pill.payoutAccount.VERIFIED': 'Verified',
  'pill.payoutAccount.RESTRICTED': 'Restricted',
  // L'argent sur les deux portails : l'etat d'un document, le compte de
  // versement d'un partenaire (ses Reglages) et une commission pas encore versee.
  'pill.document.ISSUED': 'Émis',
  'pill.document.CANCELLED': 'Annulé',
  'pill.payoutOnboarding.INCOMPLETE': 'Incomplet',
  'pill.payoutOnboarding.PENDING': 'En attente',
  'pill.payoutOnboarding.VERIFIED': 'Vérifié',
  'pill.payoutOnboarding.RESTRICTED': 'Restreint',
  'pill.commission.PENDING': 'En attente',
  'pill.commission.PAID': 'Payé',
  'pill.qrAllowance.REMAINING': {
    one: '{count} restant',
    other: '{count} restants',
  },
  'pill.qrAllowance.EXHAUSTED': 'Quota épuisé',
  'pill.apartment.ARCHIVED': 'Archivé',
  'pill.apartment.NOT_SERVABLE': 'Pas prêt',
  'pill.apartment.PAUSED': 'En pause',
  'pill.apartment.LIVE': 'Actif',
  'pill.apartment.reason.NO_BAKERY': 'Aucune boulangerie ne le livre',
  'pill.apartment.reason.NO_QR': 'Pas encore de code QR',
  // Internal Ops is English-only: the English words, because the catalogue needs the key.
  'pill.clientApartment.reason.NO_BAKERY': 'No bakery reaches it',
  'pill.clientApartment.reason.NO_QR': 'No QR code yet',
  'pill.bulkResult.DONE': 'Fait',
  'pill.bulkResult.SKIPPED': 'Ignoré',
  'pill.bulkResult.FAILED': 'Échec',
  'pill.bulkResult.reason.ALREADY_ARCHIVED': 'Déjà archivé',
  'pill.bulkResult.reason.WAS_NOT_ACTIVE': 'N’était pas actif',
  'pill.bulkResult.reason.WAS_NOT_PAUSED': 'N’était pas en pause',
  'pill.bulkResult.reason.ARCHIVED': 'Archivé',
  'pill.bulkResult.reason.ARCHIVE_FAILED': 'Archivage impossible',
  'pill.bulkResult.reason.STILL_TAKING_ORDERS': 'Accepte toujours',
  'pill.bulkResult.reason.STILL_PAUSED': 'Toujours en pause',
  'pill.bulkResult.reason.PRINT_FAILED': 'Impression impossible',
  'pill.bulkResult.reason.ALLOWANCE_USED': 'Quota épuisé',
  'pill.shop.ACTIVE': 'Actif',
  'pill.shop.SUSPENDED': 'Suspendu',
  'pill.shop.PAUSED': 'En pause',
  'pill.product.ACTIVE': 'Actif',
  'pill.product.RETIRED': 'Retiré',
  'pill.supportReason.REMOVED': 'Enlevé',
  'pill.supportReason.LOST': 'Perdu',
  'pill.supportReason.CANCELLED': 'Annulée',
  'pill.supportReason.COMPLAINT': 'Réclamation',
  'pill.supportReason.DISPUTE': 'Litige',
  'pill.supportReason.FRAUD': 'Fraude',
  'pill.supportReason.STRIPE': 'Stripe',
  'pill.supportReason.OTHER': 'Autre',
  'pill.supportReason.WASTED_RIDER': 'Livreur',
  'pill.supportReason.REFUND_REFUSED': 'Refusé',
  'pill.paymentMovement.PENDING': 'En attente',
  'pill.paymentMovement.SENT': 'Envoyé',
  'pill.paymentMovement.CONFIRMED': 'Fait',
  'pill.paymentMovement.FAILED': 'Nouvel essai',
  'pill.paymentMovement.NEEDS_HUMAN': 'Échec',
  'pill.availability.SOLD_OUT_TODAY': 'Épuisé',
  'pill.availability.reason.UNTIL_FURTHER_NOTICE': 'Jusqu’à nouvel ordre',
  'pill.teamMember.INVITED': 'Invité',
  'pill.teamMember.ACTIVE': 'Actif',
  'pill.teamMember.DISABLED': 'Désactivé',
  // Internal Ops is English-only: the English words, because the catalogue needs the key.
  'pill.formuleHouse.UNPRICED': { one: '{count} unpriced', other: '{count} unpriced' },
  'pill.formuleHouse.NEGATIVE': 'Negative',
  'pill.formuleHouse.reason.EVERY_PICK': 'On every pick',
  'pill.formuleHouse.reason.SOME_PICKS': 'On some picks',
  // La liste des commandes du client. PAS les titres d'etape du suivi
  // (`stage.*`, les mots du PRD, GST-036), qui restent sur la page commande.
  'pill.guestStage.ORDER_PLACED': 'Passée',
  'pill.guestStage.ORDER_CONFIRMED': 'Confirmée',
  'pill.guestStage.PREPARING': 'En préparation',
  'pill.guestStage.ORDER_READY': 'Prête',
  'pill.guestStage.ON_THE_WAY': 'En route',
  'pill.guestStage.DELIVERED': 'Livrée',
  'pill.guestException.CANCELLED': 'Annulée',
  'pill.guestException.reason.NO_BAKERY':
    'Aucune boulangerie n’a pu prendre votre commande. Vous serez remboursé intégralement.',
  'pill.guestException.reason.BAKERY_COULD_NOT_PREPARE':
    'Votre boulangerie n’a pas pu préparer votre commande. Vous serez remboursé intégralement.',
  'pill.guestException.reason.BAKERY_CLOSED':
    'Votre boulangerie est fermée ce jour-là. Vous serez remboursé intégralement.',
  'pill.guestException.reason.OPERATOR':
    'Notre équipe a annulé votre commande. Vous serez remboursé intégralement.',
  'pill.guestException.reason.NO_COURIER':
    'Aucun livreur n’a pu être trouvé pour votre commande. Vous serez remboursé intégralement.',
  'pill.guestException.reason.NEVER_COLLECTED':
    'Aucun livreur n’est venu chercher votre commande. Vous serez remboursé intégralement.',
  'pill.guestException.reason.COURIER_COULD_NOT_DELIVER':
    'Le livreur n’a pas pu vous livrer. Vous serez remboursé intégralement.',
  'pill.guestException.reason.DELIVERY_RETURNING':
    'Livraison échouée, votre commande retourne à la boulangerie.',
  'pill.guestException.reason.APARTMENT_REMOVED':
    'Ce logement ne prend plus de commandes. Vous serez remboursé intégralement.',
  'pill.guestException.reason.CARD_REFUSED': 'Votre carte a été refusée. Rien n’a été débité.',
  'pill.guestOrderDue.TODAY': 'Aujourd’hui',
  'pill.guestOrderDue.IN_PROGRESS': 'En cours',
  'pill.guestOrderDue.TOMORROW': 'Demain',
  'pill.guestOrderDue.IN_DAYS': {
    one: 'Dans {count} jour',
    other: 'Dans {count} jours',
  },
  'pill.stockOut.ORDER_ITEMS': {
    one: 'Épuisé · {count} article',
    other: 'Épuisé · {count} articles',
  },
  'pill.stockOut.LINE_REMOVED': 'Épuisé · Retiré',
  'pill.stockOut.UNITS_REMOVED': 'Épuisé · {count} en moins',
  'pill.stockOut.REPLACED': 'Épuisé · Remplacé',
  // The Sunmi handheld's History outcome (counterOutcome): EXACTLY the words
  // its strings.xml carries - history_outcome_<CODE> and the line under it,
  // history_reason_<CODE> - which scripts/check-counter-pills.mjs compares
  // character for character. En route, Livree and Annulee are the order's
  // own words (PILL_WORD_KEYS).
  'pill.counterOutcome.REFUSED': 'Refusée',
  'pill.counterOutcome.UNKNOWN': 'Terminée',
  'pill.counterOutcome.reason.NO_BAKERY': 'Aucune boulangerie disponible',
  'pill.counterOutcome.reason.GIVEN_BACK': 'Rendue par vous',
  'pill.counterOutcome.reason.NEVER_PREPARED': 'Non préparée à temps',
  'pill.counterOutcome.reason.CLOSURE': 'Fermeture',
  'pill.counterOutcome.reason.GUEST': 'Annulée par le client',
  'pill.counterOutcome.reason.OPERATOR': 'Annulée par nos équipes',
  'pill.counterOutcome.reason.REASSIGNED': 'Confiée à une autre boulangerie',
  'pill.counterOutcome.reason.NO_COURIER': 'Aucun livreur trouvé',
  'pill.counterOutcome.reason.NEVER_COLLECTED': 'Jamais récupérée',
  'pill.counterOutcome.reason.DELIVERY_FAILED': 'Livraison échouée',
  'pill.counterOutcome.reason.PROPERTY_REMOVED': 'Logement retiré',
  'pill.counterOutcome.reason.BY_YOU': 'Par vous',
  'pill.counterOutcome.reason.NO_ANSWER': 'Sans réponse dans le délai',

  // -------------------------------------------------------------------------
  // Order tracking
  // -------------------------------------------------------------------------
  'order.promisedWindow': 'Commande pour {day}, {window}',
  'order.deliveredOn': 'Livr\u00e9e {day} \u00e0 {time}',
  'order.orderCode': 'Commande {code}',
  'order.courierVehicle': '{make} {model} {colour}',
  'order.courierUnnamed': 'Votre livreur',
  'order.contactCourier': 'Contacter le livreur',
  'order.contactCourierPin': 'Code {pin}',
  'order.proofOfDelivery': 'Photo de livraison',
  'order.downloadReceipt': 'Télécharger le reçu',
  'order.itemCount': {
    one: '{count} article',
    other: '{count} articles',
  },
  'order.bagCount': {
    one: '{count} sac',
    other: '{count} sacs',
  },

  // -------------------------------------------------------------------------
  // Le recu du client (PRD PAY-022)
  // -------------------------------------------------------------------------
  'receipt.title': 'Reçu',
  'receipt.thanks': "Merci d'avoir passé une commande, {name}",
  'receipt.forShop': 'Voici votre reçu pour {shop}.',
  'receipt.forYou': 'Voici votre reçu.',
  'receipt.order': 'Commande {code}',
  'receipt.total': 'Total',
  'receipt.itemsSubtotal': 'Sous-total des articles',
  'receipt.deliveryFee': 'Frais de livraison',
  'receipt.serviceFee': 'Frais de service',
  'receipt.smallOrderFee': 'Frais petite commande',
  'receipt.promotion': 'Réduction',
  'receipt.refunded': 'Remboursé',
  'receipt.payments': 'Paiements',
  'receipt.cardPayment': 'Paiement par carte',
  'receipt.toPay': 'À régler',
  'receipt.orderDetails': 'Détails de la commande',
  'receipt.pickedUp': 'Prise en charge',
  'receipt.delivered': 'Livraison',
  'receipt.deliveredBy': 'Livrée par {name}',
  'receipt.orderCompleted': 'Commande terminée',

  // -------------------------------------------------------------------------
  // Orders screen
  // -------------------------------------------------------------------------
  'orders.title': 'Vos commandes',
  'orders.active': 'En cours',
  'orders.past': 'Commandes passées',
  'orders.empty': 'Aucune commande pour l’instant',
  'orders.reorder': 'Recommander',
  'orders.viewOrder': 'Voir la commande',

  // -------------------------------------------------------------------------
  // Storefront
  // -------------------------------------------------------------------------
  'storefront.title': 'Le petit-déjeuner, livré à votre porte',
  'storefront.chooseShop': 'Choisissez une boulangerie',
  'storefront.shopMinimumBasket': 'Commande minimum {amount}',
  'storefront.changeShopWarning':
    'Changer de boulangerie vide votre panier. Voulez-vous continuer ?',
  'storefront.notServable.title': 'Pas encore disponible ici',
  'storefront.notServable.body':
    'Nous ne livrons pas encore à cette adresse. De nouveaux quartiers s’ajoutent chaque mois.',
  'storefront.servicePaused.title': 'Les commandes sont en pause',
  'storefront.servicePaused.body':
    'Les commandes sont en pause pour le moment. Merci de réessayer un peu plus tard.',
  'storefront.soldOutToday': 'Épuisé aujourd’hui',
  'storefront.allergens': 'Allergènes',
  'storefront.ingredients': 'Ingrédients',

  // -------------------------------------------------------------------------
  // Types de produits (PRD CAT-016)
  // -------------------------------------------------------------------------
  'productType.MENU': 'Formules',
  'productType.EXTRA': 'Extras',
  'productType.OTHER': 'Autres',

  // -------------------------------------------------------------------------
  // Window picker
  // -------------------------------------------------------------------------
  'windows.chooseDay': 'Choisissez un jour',
  'windows.chooseWindow': 'Choisissez un créneau de 30 minutes',
  'windows.unavailable.capacity': 'Complet',
  'windows.unavailable.tooEarly': 'Trop tôt pour la boulangerie',
  'windows.unavailable.closed': 'Boulangerie fermée',

  // -------------------------------------------------------------------------
  // Checkout
  // -------------------------------------------------------------------------
  'checkout.title': 'Paiement',
  'checkout.contact': 'Vos coordonnées',
  'checkout.fullName': 'Nom complet',
  'checkout.phone': 'Numéro de mobile',
  'checkout.email': 'E-mail (facultatif)',
  'checkout.language': 'Langue',
  'checkout.deliveryDetails': 'Détails de livraison',
  'checkout.address': 'Adresse',
  'checkout.building': 'Bâtiment',
  'checkout.floor': 'Étage',
  'checkout.doorPosition': 'Position de la porte',
  'checkout.doorNumber': 'Numéro de porte',
  'checkout.intercomName': 'Nom sur l’interphone',
  'checkout.accessCode': 'Code d’accès',
  'checkout.lift': 'Ascenseur',
  'checkout.courierInstructions': 'Instructions pour le livreur',
  'checkout.additionalInstructions': 'Instructions supplémentaires pour le livreur',
  'checkout.leaveAtDoor': 'Déposer devant la porte',
  'checkout.review': 'Vérifiez votre commande',
  'checkout.pay': 'Commander et payer · {amount}',
  'checkout.terms':
    'En payant, vous acceptez nos {terms} et autorisez Croissant in Bed à débiter votre carte pour les paiements futurs conformément à celles-ci.',
  'checkout.termsLink': 'conditions générales',
  'checkout.minimumBasket': 'Cette boulangerie a une commande minimum de {amount}.',
  'checkout.minimumBasketShort': 'Ajoutez {amount} pour commander',

  // -------------------------------------------------------------------------
  // Price breakdown
  // -------------------------------------------------------------------------
  'price.merchandise': 'Articles',
  'price.delivery': 'Livraison',
  'price.service': 'Frais de service',
  'price.smallOrder': 'Frais petite commande',
  'price.promotion': 'Promotion',
  'price.tax': 'TVA',
  'price.taxLine': 'TVA {rate}',
  'price.taxIncluded': 'dont TVA',
  'price.taxIncludedAt': 'dont TVA {rate}',
  'price.total': 'Total',
  'price.totalPerMorning': 'Total par matin',
  'price.free': 'Offert',
  // GST-044: what the next step of the delivery grid saves, and the free-delivery basket.
  'price.addToSave': 'Ajoutez {amount} pour économiser {saving} sur vos frais de livraison.',
  'price.addToSaveFreeAbove':
    'Ajoutez {amount} pour économiser {saving} sur vos frais de livraison. Livraison offerte dès {free}.',
  'checkout.freeDeliveryAbove': 'Livraison offerte dès {amount}',

  // -------------------------------------------------------------------------
  // Cancellation
  // -------------------------------------------------------------------------
  'cancel.action': 'Annuler la commande',
  'cancel.confirmTitle': 'Annuler cette commande ?',
  'cancel.confirmBody': 'Votre remboursement intégral est confirmé par notre équipe.',
  'cancel.confirmAction': 'Oui, annuler',
  'cancel.keepAction': 'Garder ma commande',

  // -------------------------------------------------------------------------
  // Tip
  // -------------------------------------------------------------------------
  'tip.title': 'Un pourboire pour votre livreur ?',
  'tip.body': '100 % va au livreur.',
  'tip.custom': 'Autre montant',
  'tip.skip': 'Non merci',
  'tip.confirm': 'Donner {amount}',
  'tip.thanks': 'Merci. Envoyé à {name}.',

  // -------------------------------------------------------------------------
  // Ratings
  // -------------------------------------------------------------------------
  'rating.title': 'Comment ça s’est passé ?',
  'rating.delivery': 'La livraison',
  'rating.products': 'Les produits',
  'rating.comment': 'Un mot à ajouter ?',
  'rating.submit': 'Envoyer',
  'rating.skip': 'Passer',
  'rating.thanks': 'Merci.',
  'rating.stars': {
    one: '{count} étoile',
    other: '{count} étoiles',
  },

  // -------------------------------------------------------------------------
  // Issue reporting
  // -------------------------------------------------------------------------
  'issue.title': 'Signaler un problème',
  'issue.selectItems': 'Quels articles ?',
  'issue.describe': 'Que s’est-il passé ?',
  'issue.addPhoto': 'Ajouter une photo',
  'issue.submit': 'Envoyer le signalement',
  'issue.submitted': 'Merci. Nous examinons cela.',

  // -------------------------------------------------------------------------
  // Repeat schedules
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // Notifications
  // -------------------------------------------------------------------------
  'notification.orderConfirmed':
    'Votre petit-déjeuner est confirmé pour {day}, {window}. Suivi : {link}',
  'notification.preparationIssue':
    'Il y a un souci avec votre commande. Nous nous en occupons : {link}',
  // Tournure sans participe accordé : le nom du produit peut être masculin ou
  // féminin et arrive comme donnée, pas comme copie traduite.
  'notification.itemReplaced':
    '{original} est en rupture : votre commande contient {replacement} à la place. Détails : {link}',
  'notification.itemRemoved':
    'La boulangerie n’avait plus de {product} (x{units}). Vous ne le paierez pas. Détails : {link}',
  'notification.pickupDelayed':
    'Votre boulangerie a besoin d’un peu plus de temps. Votre petit-déjeuner arrive maintenant {window}. Détails : {link}',
  'notification.onTheWay': '{name} a récupéré votre petit-déjeuner et arrive. Suivi : {link}',
  'notification.arriving': '{name} est tout près avec votre petit-déjeuner. Suivi : {link}',
  'notification.delivered': 'Votre petit-déjeuner a été livré. Bon appétit ! Un souci ? {link}',
  'notification.cancelled':
    'Votre commande a été annulée. Votre remboursement est confirmé par notre équipe. Détails : {link}',
  'notification.orderUnfulfilled':
    'Désolés — aucune boulangerie n’a pu prendre votre commande. Elle est annulée et tout remboursement est confirmé par notre équipe. Détails : {link}',
  'notification.refundSentence': 'Le remboursement sera effectué. Détails : {link}',
  'notification.issueResolved': 'Nous avons résolu le problème sur votre commande : {link}',
  'notification.supportReply': 'À propos de votre commande : {text} Détails : {link}',
  'notification.paymentFailed':
    'Nous n’avons pas pu débiter votre carte pour demain matin. Corriger : {link}',
  'notification.deliveryFailed':
    'Nous n’avons pas pu mener votre livraison à bien. Nous nous en occupons — détails et suite : {link}',
  'notification.courierSearchDelayed':
    'Trouver un livreur prend plus de temps que prévu. Nous continuons de chercher : {link}',
  'notification.dataRequestCode':
    'Votre code de confirmation Croissant in Bed est {code}. Il expire dans 15 minutes.',
  'documents.guestInvoicesSubject': 'Vos factures — commande {code}',
  'documents.guestInvoicesBody':
    'Vos factures pour la commande {code} sont jointes à ce message, au format PDF. Elles sont aussi sur la page de votre commande.',

  // -------------------------------------------------------------------------
  // Generic actions and errors
  // -------------------------------------------------------------------------
  'action.continue': 'Continuer',
  'action.back': 'Retour',
  'action.save': 'Enregistrer',
  'action.cancel': 'Annuler',
  'action.close': 'Fermer',
  'action.retry': 'Réessayer',
  'action.addToBasket': 'Ajouter',
  'action.removeFromBasket': 'Retirer',
  'error.network': 'Impossible de joindre le serveur.',
  'error.unexpected': 'Une erreur est survenue. Merci de réessayer.',
  'error.temporarily_unavailable':
    'Nos systèmes sont momentanément injoignables. Merci de réessayer dans un instant.',
  'error.notFound': 'Introuvable.',
  'error.unauthorized': 'Votre session n’est plus valide. Reconnectez-vous.',
  'error.forbidden': 'Vous n’avez pas accès à cet élément.',
  'error.conflict': 'Quelque chose a changé pendant votre saisie. Actualisez puis réessayez.',
  'error.provider_unavailable': 'Le fournisseur de livraison ne répond pas.',
  'error.unknown_provider': 'Ce prestataire de livraison n’est pas configuré sur ce déploiement.',
  'error.windowUnavailable': 'Ce créneau n’est plus disponible. Merci d’en choisir un autre.',
  'error.capacityExhausted': 'La boulangerie est complète sur ce créneau.',
  'error.minimumBasket': 'Votre panier est en dessous du minimum de cette boulangerie.',
  'error.paymentFailed': 'Votre paiement n’a pas abouti. Merci d’essayer une autre carte.',
  'error.servicePaused': 'Les commandes sont en pause pour le moment.',
  'error.notServable': 'Nous ne livrons pas encore à cette adresse.',
  'error.cancellationClosed': 'Trop tard pour annuler.',

  // -------------------------------------------------------------------------
  // Wire error vocabulary - see the English catalogue for what this section is.
  // -------------------------------------------------------------------------

  'error.validation': 'Corrigez les champs indiqués.',
  'error.idempotency_key_required':
    'Cette action n’a pas pu être envoyée en toute sécurité. Merci de réessayer.',
  'error.idempotency_conflict': 'Cette clé a déjà servi à une autre demande. Recommencez l’action.',
  'error.support_case_stale': 'La commande a changé. Actualisez et décidez à nouveau.',
  'error.refund_over_charged':
    'Ce montant dépasse ce que la commande a coûté, compte tenu des autres dossiers.',
  'error.support_case_invalid': 'Cette décision ne peut pas être prise dans cet état.',
  'error.refund_unauthorized': 'Un opérateur interne authentifié doit autoriser cette action.',
  'error.refund_order_not_final':
    'Un dossier ne peut être ouvert qu’une fois la commande clôturée ou annulée.',
  'error.support_case_not_found': 'Le dossier est introuvable.',
  'error.internal': 'Une erreur est survenue de notre côté. Merci de réessayer.',
  'error.http': 'Cette requête n’a pas pu aboutir.',
  'error.domain': 'Ce n’est pas possible pour le moment.',
  'error.cancellation_closed': 'Trop tard pour annuler.',
  'error.acceptance_not_open': "Cette commande n'est pas encore ouverte à la réponse.",
  'error.rejection_reason_required': 'Un motif est obligatoire pour refuser cette commande.',
  'error.guest_choice_required':
    'Le client a choisi cette boulangerie : elle ne peut pas être réattribuée sans son accord.',
  'error.immediate_only': 'Ceci n’est possible que pour les commandes immédiates.',
  'error.stale_event': 'Cette mise à jour est arrivée dans le désordre et a été ignorée.',
  'error.provider_limit': 'Le partenaire de livraison refuse ces valeurs.',
  'error.provider_update_ignored':
    'Le partenaire de livraison n’a pas appliqué cette modification.',
  'error.capability_unavailable': 'Le partenaire de livraison ne propose pas cette fonction.',
  'error.illegal_transition': 'Cette commande a déjà changé d’état.',
  'error.qr_invalid': 'Ce code n’est pas reconnu.',
  'error.session_invalid': 'Cette session a expiré. Scannez à nouveau le code.',
  'error.order_not_found': 'Cette commande n’existe pas.',
  'error.window_unavailable': 'Ce créneau n’est plus disponible. Merci d’en choisir un autre.',
  'error.delivery_unavailable': 'La livraison est indisponible pour le moment.',
  'error.window_passed': 'Ce créneau est passé.',
  'error.courier_quote_above_ceiling':
    'La livraison à cette adresse coûte trop cher pour le moment. Merci de réessayer à un autre moment.',
  'error.stay_morning_unavailable':
    'L’un de vos matins n’est plus disponible. Vérifiez votre commande avant de payer.',
  'error.ordering_closed': 'Les commandes sont fermées pour le moment.',
  'error.courier_en_route': 'Le coursier est déjà en route',
  'error.bag_photo_required': 'Une photo du sachet est obligatoire.',
  'error.bag_photo_not_ready': 'Cette commande n’est pas prête pour une photo du sachet.',
  'error.bag_photo_limit': 'Cette commande a déjà 10 photos du sachet.',
  'error.no_bakery': 'Aucune boulangerie ne peut servir ce matin-là.',
  'error.choose_shop': 'Choisissez d’abord une boulangerie.',
  'error.shop_changed': 'La boulangerie qui vous sert a changé. Votre panier a été vidé.',
  'error.minimum_basket': 'Votre panier est en dessous du minimum de cette boulangerie.',
  'error.payment_declined': 'Votre paiement n’a pas abouti. Merci d’essayer une autre carte.',
  'error.tip_unsupported': 'Le pourboire n’est pas disponible pour cette livraison.',
  'error.tip_window_closed': 'La période pour laisser un pourboire est terminée.',
  'error.rating_before_delivery': 'Vous pourrez noter cette commande une fois qu’elle sera livrée.',
  'error.order_not_closed': 'Vos factures seront disponibles une fois cette commande terminée.',
  'error.not_dispatchable': 'Cette commande ne peut pas encore partir en livraison.',
  'error.order_ended': 'Cette commande est terminée ; sa livraison ne peut plus être modifiée.',
  'error.qr_cap': 'Le quota de codes de ce compte est épuisé.',
  'error.import_invalid': 'Ce fichier n’a pas pu être importé. Vérifiez les colonnes et réessayez.',
  'error.unknown_entity': 'Cet enregistrement n’existe pas.',

  'error.code_expired': 'Ce code a expiré. Demandez-en un nouveau.',
  'error.code_invalid': 'Ce code n’est pas correct.',
  'error.tip_failed': 'Pourboire refusé. Essayez une autre carte.',
  'guest.not_servable': 'Nous ne livrons pas encore à cette adresse.',
  'guest.service_paused': 'Les commandes sont en pause pour le moment.',

  'error.address_outside_paris':
    'Nous ne desservons que Paris pour l’instant, et cette adresse est en dehors. Vérifiez le code postal et la ville.',
  'error.bad_base64': 'Ce scan n’a pas pu être lu.',
  'error.batch_not_found': 'Ce lot de codes n’existe pas.',
  'error.geocode_failed': 'Cette adresse n’a pas pu être localisée. Vérifiez-la et réessayez.',
  'error.import_not_found': 'Cet import n’existe pas ou a expiré.',
  'error.member_not_found': 'Ce membre de l’équipe n’existe pas.',
  'error.member_address_unknown':
    'L’adresse de ce membre est inconnue : son accès ne peut pas être modifié ici.',
  'error.member_already_in_team': 'Cette personne est déjà membre.',
  'error.member_last_admin':
    'C’est le dernier administrateur de l’organisation : son accès ne peut pas être retiré.',
  'error.member_address_rejected': 'Le service de connexion a refusé cette adresse.',
  'error.member_disabled': 'Votre accès à cette organisation a été désactivé.',
  'error.member_self_disable': 'Vous ne pouvez pas désactiver votre propre accès.',
  'error.member_self_remove': "Vous ne pouvez pas vous retirer vous-même de l'équipe.",
  'error.team_read_only': "La liste de l'équipe de cette organisation est en lecture seule.",
  'error.apartment_archived': 'Un appartement archivé ne peut pas être rétabli.',
  'error.team_not_connected':
    'Cette organisation n’est pas reliée au service de connexion : son équipe ne peut pas être modifiée ici.',
  'error.team_directory_unavailable':
    'Le service de connexion n’a pas répondu. Réessayez dans un instant.',
  'error.payout_not_found': 'Ce versement n’existe pas.',
  'error.payout_held': 'Les versements de cette organisation sont suspendus.',
  'error.payout_nothing_available': 'Rien n’est disponible à verser.',
  'error.payout_identity_incomplete': 'L’identité légale que sa facture exige est incomplète.',
  'error.payout_account_not_verified': 'Aucun compte de versement vérifié.',
  'error.payout_contact_missing': 'Aucun contact de versement enregistré.',
  'error.payout_bank_details_needed': 'De nouvelles coordonnées bancaires sont nécessaires.',
  'error.payout_account_restricted': 'Stripe a restreint ce compte. Le versement attend.',
  'error.payout_account_changed': 'Le compte de versement a changé. Prévisualisez à nouveau.',
  'error.payout_preview_stale': 'Un autre versement est en cours. Prévisualisez à nouveau.',
  'error.document_not_exportable': 'Ce document n’est pas en attente d’envoi à Pennylane.',
  'error.document_not_reissuable': 'Ce document ne peut pas être annulé et réémis.',
  'error.invoice_identity_incomplete': 'L’identité légale de la boulangerie est incomplète.',
  'error.invoice_not_created': 'Créez d’abord la facture.',
  'error.delivery_statement_open': 'Ce cycle est encore ouvert.',
  'error.delivery_statement_matched': 'Ce relevé est déjà rapproché d’une autre facture.',
  'error.delivery_statement_changed':
    'Une autre facture vient d’être saisie. Rechargez et réessayez.',
  'error.properties_not_found': 'Certains de ces appartements n’existent pas.',
  'error.property_not_found': 'Cet appartement n’existe pas.',
  'error.qr_cap_reached':
    'Le quota de codes de ce compte est atteint. Contactez-nous pour l’augmenter.',
  'error.storefront_mode_not_allowed':
    'Ce type de menu n’est pas disponible pour cette organisation. Demandez à Croissant in Bed de l’activer.',
  'error.qr_not_generated': 'Aucun code n’a encore été généré pour cet appartement.',
  'error.no_qr_codes': 'Aucun appartement n’a encore de QR code.',

  'error.line_not_found': 'Cette ligne ne figure pas sur cette commande.',
  'error.not_collectable': 'Aucun sac n’attend encore de coursier pour cette commande.',
  'error.replacement_not_approved': 'Ce remplacement n’a pas été validé.',
  'error.quantity_above_ordered': 'C’est plus que ce que le client a commandé.',
  'error.quantity_already_reduced': 'Ces articles ont déjà été remboursés.',
  'error.replacement_unavailable': 'Ce remplacement n’est pas disponible.',
  'error.delay_not_available':
    'Le temps supplémentaire ne peut être pris que tant que la commande est au comptoir, avant qu’un coursier soit affecté.',
  'error.cancel_not_available':
    'Cette commande ne peut plus être annulée depuis le comptoir : le coursier a le sac.',

  'error.alert_not_found': 'Cette alerte n’existe pas.',
  'error.bakery_not_found': 'Cette boulangerie n’existe pas.',
  'error.bakery_org_mode_mismatch': 'Cette maison ne vend pas dans ce mode.',
  'error.bakery_org_required': 'Choisissez la maison à laquelle cette boutique appartient.',
  'error.has_pending_orders':
    'Des commandes sont encore en cours. Supprimez-la une fois qu’elles sont terminées.',
  'error.delivery_not_rebookable': 'Cette livraison ne peut pas être reprogrammée.',
  'error.courier_assigned': 'Un livreur a déjà pris cette course : rien n’a été modifié.',
  'error.provider_cancel_refused':
    'Le prestataire de livraison a refusé d’annuler la course : rien n’a été reprogrammé.',
  // Les trois refus de l'onboarding des versements (OPS, PAY-032). Ils
  // s'affichent pour un opérateur sur la fiche de la maison.
  'error.house_not_trading': 'Réactivez cette maison avant d’envoyer un lien d’inscription.',
  'error.legal_identity_incomplete': 'Complétez l’identité légale avant d’envoyer un lien.',
  'error.payout_provider_unavailable': 'Stripe n’a pas créé le compte. Réessayez.',
  'error.kill_city_required': 'Une ville est obligatoire pour une coupure à l’échelle d’une ville.',
  'error.order_line_unknown': 'Cette ligne ne figure pas sur cette commande.',
  'error.org_not_found': 'Cette organisation n’existe pas.',
  'error.org_code_taken': 'Une autre organisation a déjà ce code.',
  'error.org_code_reserved': 'Ce code appartient à un prestataire de livraison.',
  'error.org_code_frozen': 'Ce code est déjà utilisé et ne peut plus changer.',
  'error.partner_not_found': 'Ce partenaire n’existe pas.',
  'error.plan_unavailable': 'Cette confirmation n’est plus valable. Relancez l’aperçu.',
  'error.rate_amount_required': 'Un montant est obligatoire pour ce tarif.',
  'error.rate_kind': 'Ce type de tarif n’est pas valide ici.',
  'error.rate_scope_required': 'Une commission désigne le partenaire qui la perçoit.',
  // La revue de responsabilité et la réclamation transporteur (PAY-013, PAY-030).
  'error.no_open_review': 'Aucune revue de responsabilité n’est ouverte sur cette commande.',
  'error.decision_amount_required': 'Un remboursement partiel exige un montant.',
  'error.decision_amount_too_large':
    'Ce montant d\u00e9passe ce que ce dossier de remboursement porte.',
  'error.review_not_open_yet':
    'La commande n’est pas terminée. Une décision de remboursement attend que le pain ait cessé de bouger.',
  'error.claim_action_unavailable': 'La réclamation ne peut pas évoluer ainsi depuis son état.',
  'error.decision_unavailable':
    'Cette réponse ne fait pas partie de celles que ce dossier de remboursement propose.',
  'error.partial_claim':
    'Une réclamation transporteur rend la totalité de ce que le dossier porte.',
  'error.target_suspended': 'Cette boutique est suspendue et ne prend aucune commande.',
  'error.target_mode_incompatible': 'Cette maison vend dans l’autre mode de vitrine.',
  'error.target_other_house':
    'Le client a choisi cette maison. La commande ne peut aller que vers une autre de ses boutiques.',
  'error.target_unpriced': 'Cette maison n’a convenu aucun prix pour un article de ce panier.',
  'error.target_closed': 'Cette boutique est fermée le jour de cette commande.',
  'error.refund_amount_unsupported':
    'Un montant partiel n’est pas pris en charge pour ce remboursement.',
  'error.small_order_fee_incomplete':
    'Des frais petite commande exigent à la fois un seuil de panier et un montant.',
  'error.catalogue_item_not_found': 'Ce produit n\u2019existe plus.',
  'error.media_too_large':
    'Cette image est trop volumineuse pour ce d\u00e9ploiement. Utilisez un fichier plus petit.',
  'error.pool_not_writable':
    'Seuls le rayon plateforme et la gamme propre d\u2019une maison en mode B sont modifiables.',
  'error.legal_identity_platform_managed':
    'Pour modifier ces informations, contactez Croissant in Bed.',
  'error.tax_kind_locked': 'Pour modifier votre statut fiscal, contactez Croissant in Bed.',
  'error.stripe_identity_locked':
    'Ces informations ont été vérifiées par Stripe et ne peuvent plus être modifiées. Pour en changer, créez un nouveau compte.',
  'error.payout_details_incomplete':
    'Il manque encore des informations. Vérifiez le formulaire et réessayez.',
  'error.organization_suspended':
    'Cette organisation est suspendue. Contactez-nous pour la réactiver.',

  // Les types dont le menu se compose (CAT-016).
  'error.product_type_exists': 'Un type porte d\u00e9j\u00e0 ce nom.',
  'error.product_type_not_found': 'Ce type n\u2019existe plus.',
  'error.product_type_in_use': 'D\u00e9placez ou retirez d\u2019abord les produits de ce type.',
  'error.product_type_built_in':
    'Ce type fait partie du produit : il ne peut \u00eatre ni renomm\u00e9 ni supprim\u00e9.',
  'error.product_type_name_required': 'Un type a besoin d\u2019un nom anglais.',

  // Le canal de mise a jour des terminaux (OPS-029). Voir la note dans en.ts :
  // les memes phrases que le catalogue du portail interne, mot pour mot.
  'error.handheld_release_exists':
    'Une version portant ce num\u00e9ro est d\u00e9j\u00e0 enregistr\u00e9e.',
  'error.handheld_release_not_found': 'Cette version n\u2019existe plus.',
  'error.handheld_version_not_above':
    'Une version doit \u00eatre plus r\u00e9cente que celle d\u00e9j\u00e0 publi\u00e9e : les comptoirs refusent toute version ant\u00e9rieure.',
  'error.handheld_version_below_minimum':
    'La version minimale impos\u00e9e ne fait que monter. Retirez d\u2019abord la contrainte pour revenir \u00e0 une version plus ancienne.',
  'error.handheld_not_latest':
    'Seule la version actuellement propos\u00e9e au parc peut devenir la version minimale. Publiez d\u2019abord celle-ci.',
  'error.handheld_release_unsigned':
    'Cette version n\u2019est pas encore sign\u00e9e et chaque comptoir la rejetterait. Signez d\u2019abord le manifeste.',
  'error.handheld_release_halted':
    'Cette version a \u00e9t\u00e9 arr\u00eat\u00e9e et ne peut pas \u00eatre publi\u00e9e.',
  'error.handheld_release_not_published':
    'Seule une version publi\u00e9e peut devenir la version minimale.',
  'error.handheld_manifest_mismatch':
    'Ce manifeste ne d\u00e9crit pas cette version. Collez celui que la plateforme a affich\u00e9 pour elle.',
  'error.handheld_manifest_channel':
    'Ce manifeste a \u00e9t\u00e9 sign\u00e9 pour l\u2019autre environnement.',
  'error.handheld_signature_invalid':
    'Cette signature n\u2019a pas \u00e9t\u00e9 faite avec la cl\u00e9 annonc\u00e9e. V\u00e9rifiez quelle cl\u00e9 a sign\u00e9.',
  'error.handheld_rollout_incomplete':
    'Cette version n\u2019a pas encore atteint tout le parc. \u00c9largissez-la \u00e0 100 % avant de l\u2019imposer.',
  'error.handheld_confirmation_mismatch': 'Saisissez le num\u00e9ro de version pour confirmer.',
  'error.handheld_location_unknown': 'Une des boutiques choisies n\u2019existe plus.',
  'error.handheld_threshold_pair':
    'Un seuil de r\u00e9solution doit laisser un \u00e9cart : celui de l\u2019horloge passe sous son seuil de d\u00e9clenchement, celui du stockage au-dessus du sien.',
  'error.handheld_device_not_found': 'Cet appareil comptoir n\u2019existe plus.',
  'error.handheld_nothing_published':
    'Rien n\u2019est publi\u00e9, il n\u2019y a donc aucun d\u00e9ploiement \u00e0 \u00e9largir.',
  'error.handheld_rollout_not_widening':
    'Un d\u00e9ploiement ne fait que s\u2019\u00e9largir : choisissez une part plus grande.',
  'error.handheld_release_not_halted': 'Cette version n\u2019\u00e9tait pas arr\u00eat\u00e9e.',
  'error.handheld_diagnostic_not_found': 'Ce document de diagnostic n\u2019existe plus.',

  // Lecture des traces d'un scenario dans le bucket RUNS (OPS-038).
  'error.run_evidence_unavailable':
    'Le stockage des traces d\u2019ex\u00e9cution n\u2019est pas configur\u00e9 sur cette instance.',
  'error.run_not_found': 'Aucune trace n\u2019a \u00e9t\u00e9 publi\u00e9e sous cet identifiant.',
  'error.run_shot_not_found': 'Cette capture ne fait pas partie de ces traces.',
  'error.whatsapp_not_configured': 'WhatsApp n’est pas encore configuré sur cette instance.',
  'error.whatsapp_unreachable': 'WhatsApp n’a pas répondu. Réessayez dans une minute.',

  // Composition d'une formule et prix par maison (CAT-017, CAT-018).
  'error.not_a_formule': 'Seule une formule est compos\u00e9e d\u2019autres produits.',
  'error.formule_in_formule': 'Une formule ne peut pas contenir une autre formule.',
  'error.formule_answers': 'Choisissez une option pour chaque partie de cette formule.',
  'error.formule_without_components':
    'Une formule doit contenir au moins un article avant de figurer au menu.',
  'error.product_code_taken': 'Ce code produit d\u00e9signe d\u00e9j\u00e0 un autre produit.',
  'error.choice_not_in_pool':
    'Un choix doit \u00eatre un produit d\u00e9j\u00e0 pr\u00e9sent au menu.',
  'error.choice_retired': 'Un produit retir\u00e9 ne peut pas \u00eatre propos\u00e9 comme choix.',
  'error.choice_in_published_formule':
    'Une formule au menu contient encore ce produit. Retirez-le de la formule d\u2019abord.',
  'error.section_empty': 'Une section doit contenir au moins un produit.',
  'error.choice_needs_options': 'Une section au choix doit proposer au moins deux options.',
  'error.negative_commission': 'Le prix boulangerie ne peut pas d\u00e9passer le prix client.',
  'error.guest_price_below_bakery_price':
    'Ce prix client est inf\u00e9rieur \u00e0 ce que garde une boulangerie pour ce produit. Baissez d\u2019abord son prix.',
  'error.price_follows_parts':
    'Le prix de cette formule suit celui de ses produits et ne peut pas \u00eatre modifi\u00e9.',
  'error.set_parts_unpriced':
    'Fixez d\u2019abord le prix boulangerie de chaque produit de cette formule.',
  'error.bakery_price_required': 'Saisissez le prix boulangerie.',

  // Formulaires du site public (WEB-025, WEB-026). Voir la note dans en.ts.
  'error.bot_check_failed': 'La v\u00e9rification anti-robot n\u2019a pas abouti. R\u00e9essayez.',
  'error.bot_check_unavailable':
    'La v\u00e9rification anti-robot est indisponible. R\u00e9essayez dans un instant.',
};
